/**
 * Event Catalog - Catálogo de eventos de seguridad
 *
 * Define eventos de seguridad con sus severidades, CVSS baseline/fallback,
 * OWASP Top 10 2025, CWE y MITRE ATT&CK con un enfoque heurístico alineado a estándares de industria.
 * 
*/

export const EVENT_CATALOG = {
	xss: {
		baseSeverity: 'high',
		cvssBaseScore: 7.2,
		owaspCategory: 'A05:2025 – Injection',
		cweId: 'CWE-79',
		mitreTechnique: 'T1059.007',
		variants: {
			reflected: {
				severity: 'medium',
				cvssBaseScore: 6.1
			},
			stored: {
				severity: 'high',
				cvssBaseScore: 7.2
			},
			dom_based: {
				severity: 'medium',
				cvssBaseScore: 6.4
			},
			script_tag: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			javascript_protocol: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			event_handler: {
				severity: 'medium',
				cvssBaseScore: 6.1
			},
			cookie_theft: {
				severity: 'high',
				cvssBaseScore: 8.0
			},
			iframe_object_embed: {
				severity: 'high',
				cvssBaseScore: 7.2
			},
			style_expression: {
				severity: 'medium',
				cvssBaseScore: 6.0
			},
			style_url_js: {
				severity: 'medium',
				cvssBaseScore: 6.0
			},
			meta_refresh: {
				severity: 'medium',
				cvssBaseScore: 5.8
			},
			base_tag: {
				severity: 'medium',
				cvssBaseScore: 5.8
			},
			obfuscation: {
				severity: 'medium',
				cvssBaseScore: 5.9
			},
			data_protocol: {
				severity: 'high',
				cvssBaseScore: 7.2
			},
			vbscript: {
				severity: 'high',
				cvssBaseScore: 7.4
			},			
			svg_onload: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			unicode_obfuscation: {
				severity: 'medium',
				cvssBaseScore: 6.5
			},
			html_entity_obfuscation: {
				severity: 'medium',
				cvssBaseScore: 6.3
			},
			template_literal: {
				severity: 'high',
				cvssBaseScore: 7.6
			},
			mxss_pattern: {
				severity: 'high',
				cvssBaseScore: 7.5
			},
			function_constructor: {
				severity: 'high',
				cvssBaseScore: 7.6
			},
			document_write: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			timer_eval: {
				severity: 'high',
				cvssBaseScore: 7.3
			},
			dangerously_set_inner_html: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			// <iframe srcdoc="..."> bypasses src-based CSP rules and sandbox without allow-scripts
			srcdoc_iframe: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			// MathML injection vector — used in mXSS bypass chains against HTML sanitizers
			mathml_xss: {
				severity: 'medium',
				cvssBaseScore: 6.8
			},
			// data:image/svg+xml;base64, — base64-encoded SVG with embedded XSS payload
			base64_data_uri: {
				severity: 'high',
				cvssBaseScore: 7.6
			},
			// Double-percent-encoded characters (%253c, %2527) — bypasses single-pass URL decoders
			nested_encoding: {
				severity: 'high',
				cvssBaseScore: 7.5
			}
		}
	},

	csrf: {
		baseSeverity: 'high',
		cvssBaseScore: 7.8,
		owaspCategory: 'A01:2025 – Broken Access Control',
		cweId: 'CWE-352',
		mitreTechnique: null,
		variants: {
			post_without_token: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			sensitive_endpoint: {
				severity: 'high',
				cvssBaseScore: 8.1
			},
			financial_operation: {
				severity: 'high',
				cvssBaseScore: 8.4
			},
			authentication: {
				severity: 'high',
				cvssBaseScore: 8.0
			},
			api_endpoint: {
				severity: 'high',
				cvssBaseScore: 7.6
			},
			file_operation: {
				severity: 'high',
				cvssBaseScore: 7.9
			}
		}
	},

	sql_injection: {
		baseSeverity: 'critical',
		cvssBaseScore: 8.6,
		owaspCategory: 'A05:2025 – Injection',
		cweId: 'CWE-89',
		mitreTechnique: 'T1190',
		variants: {
			union_select: {
				severity: 'critical',
				cvssBaseScore: 8.8
			},
			drop_table: {
				severity: 'critical',
				cvssBaseScore: 8.9
			},
			truncate_table: {
				severity: 'critical',
				cvssBaseScore: 8.9
			},
			delete_from: {
				severity: 'critical',
				cvssBaseScore: 8.8
			},
			termination_drop: {
				severity: 'critical',
				cvssBaseScore: 8.9
			},
			sleep_benchmark: {
				severity: 'high',
				cvssBaseScore: 8.0
			},
			or_equals: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			comment_dash: {
				severity: 'high',
				cvssBaseScore: 7.5
			},
			comment_hash: {
				severity: 'high',
				cvssBaseScore: 7.5
			},
			comment_block: {
				severity: 'high',
				cvssBaseScore: 7.5
			},
			always_true: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			concat_or_concat: {
				severity: 'high',
				cvssBaseScore: 7.9
			},
			subquery_in: {
				severity: 'high',
				cvssBaseScore: 7.9
			},
			hex_char: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			or_1_equals_1: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			or_true: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			single_quote_escape: {
				severity: 'high',
				cvssBaseScore: 7.5
			},
			double_dash: {
				severity: 'high',
				cvssBaseScore: 7.5
			},
			like_wildcard: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			waitfor_delay: {
				severity: 'high',
				cvssBaseScore: 8.0
			},
			exec_xp_cmdshell: {
				severity: 'critical',
				cvssBaseScore: 9.0
			},
			information_schema: {
				severity: 'high',
				cvssBaseScore: 7.7
			},
			extractvalue_inject: {
				severity: 'high',
				cvssBaseScore: 8.2
			},
			updatexml_inject: {
				severity: 'high',
				cvssBaseScore: 8.2
			},
			floor_rand: {
				severity: 'high',
				cvssBaseScore: 7.9
			},
			exp_tilde: {
				severity: 'high',
				cvssBaseScore: 8.0
			},
			// Oracle-specific
			oracle_chr_concat: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			dbms_pipe: {
				severity: 'high',
				cvssBaseScore: 8.0
			},
			// MSSQL-specific
			openrowset: {
				severity: 'critical',
				cvssBaseScore: 8.9
			},
			bulk_insert: {
				severity: 'high',
				cvssBaseScore: 8.4
			},
			xp_regread: {
				severity: 'high',
				cvssBaseScore: 8.2
			},
			// PostgreSQL
			pg_sleep: {
				severity: 'high',
				cvssBaseScore: 8.0
			},
			// Tautology extensions
			or_x_equals_x: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			double_quote_tautology: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			// MySQL file operations
			load_file: {
				severity: 'critical',
				cvssBaseScore: 8.8
			},
			into_outfile: {
				severity: 'critical',
				cvssBaseScore: 9.0
			},
			order_by_num: {
				severity: 'high',
				cvssBaseScore: 7.5
			},
			having_inject: {
				severity: 'high',
				cvssBaseScore: 7.7
			},
			case_when_inject: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			// Oracle OOB exfiltration
			utl_http_oracle: {
				severity: 'critical',
				cvssBaseScore: 8.9
			},
			// MSSQL dynamic execution
			sp_executesql: {
				severity: 'critical',
				cvssBaseScore: 8.8
			},			
			stacked_queries: {
				severity: 'critical',
				cvssBaseScore: 8.9
			},
			boolean_blind_and: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			boolean_blind_or: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			// INTO OUTFILE with web-shell extension — SQL-to-RCE filesystem write
			outfile_webshell_pattern: {
				severity: 'critical',
				cvssBaseScore: 9.3
			},
			// MySQL version-gated comment /*!50000 ...*/ — WAF bypass technique
			mysql_version_comment: {
				severity: 'high',
				cvssBaseScore: 8.0
			},
			mssql_waitfor_time: {
				severity: 'high',
				cvssBaseScore: 8.0
			},
			// PostgreSQL COPY ... TO/FROM PROGRAM — arbitrary OS command via SQL
			postgres_copy_program: {
				severity: 'critical',
				cvssBaseScore: 9.3
			}
		}
	},

	command_injection: {
		baseSeverity: 'critical',
		cvssBaseScore: 8.8,
		owaspCategory: 'A05:2025 – Injection',
		cweId: 'CWE-78',
		mitreTechnique: 'T1059.004', // Most representative parent mapping for shell-like command execution patterns.
		variants: {
			dollar_paren: {
				severity: 'critical',
				cvssBaseScore: 8.9
			},
			eval_call: {
				severity: 'critical',
				cvssBaseScore: 8.3,
				mitreTechnique: 'T1059.007'
			},
			pipe_cmd: {
				severity: 'high',
				cvssBaseScore: 8.3
			},
			backtick: {
				severity: 'high',
				cvssBaseScore: 8.4
			},
			semicolon_cmd: {
				severity: 'high',
				cvssBaseScore: 8.4
			},
			and_operator: {
				severity: 'high',
				cvssBaseScore: 8.3
			},
			or_operator: {
				severity: 'high',
				cvssBaseScore: 8.3
			},
			encoded_and: {
				severity: 'high',
				cvssBaseScore: 8.4
			},
			encoded_pipe: {
				severity: 'high',
				cvssBaseScore: 8.4
			},
			newline_inject: {
				severity: 'high',
				cvssBaseScore: 8.0
			},
			windows_cmd: {
				severity: 'critical',
				cvssBaseScore: 8.7,
				mitreTechnique: 'T1059.003' // Windows Command Shell
			},
			powershell: {
				severity: 'critical',
				cvssBaseScore: 8.9,
				mitreTechnique: 'T1059.001' // PowerShell
			},
			env_expansion: {
				severity: 'medium',
				cvssBaseScore: 7.5
			},
			// Shell interpreter injection
			interpreter_inject: {
				severity: 'critical',
				cvssBaseScore: 9.0,
				mitreTechnique: 'T1059.004'
			},
			// CRLF injection
			crlf_inject: {
				severity: 'high',
				cvssBaseScore: 7.9
			},
			// Block 2 — CMDi robustness expansion
			// Nested subshell $($(cmd)) — multiple evaluation layers used in obfuscation chains
			subshell_nested: {
				severity: 'critical',
				cvssBaseScore: 9.0
			},
			// curl/wget fetching external URL — arbitrary payload download for chained execution
			curl_wget_chain: {
				severity: 'critical',
				cvssBaseScore: 8.9
			},
			// netcat reverse shell: nc -e /bin/sh <ip> <port>
			nc_reverse_shell: {
				severity: 'critical',
				cvssBaseScore: 9.3
			},
			// Windows certutil used as LOLBin for URL download and payload delivery
			certutil_download: {
				severity: 'critical',
				cvssBaseScore: 8.8,
				mitreTechnique: 'T1105'
			},
			// nslookup in request parameter — DNS-based data exfiltration probe
			dns_exfil_pattern: {
				severity: 'high',
				cvssBaseScore: 8.3
			}
		}
	},

	path_traversal: {
		baseSeverity: 'high',
		cvssBaseScore: 7.2,
		owaspCategory: 'A01:2025 – Broken Access Control',
		cweId: 'CWE-22',
		mitreTechnique: 'T1083',
		variants: {
			parent_dir_slash: {
				severity: 'high',
				cvssBaseScore: 7.2
			},
			double_encoded: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			triple_encoded: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			null_byte: {
				severity: 'high',
				cvssBaseScore: 7.6
			},
			semicolon_bypass: {
				severity: 'medium',
				cvssBaseScore: 6.8
			},
			utf8_overlong: {
				severity: 'high',
				cvssBaseScore: 7.6
			},
			utf8_overlong_slash: {
				severity: 'high',
				cvssBaseScore: 7.6
			},
			four_dot_slash: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			// Block 2 — Path Traversal robustness expansion
			// Mixed forward/backslash: ..\/ or ..\/ — bypasses single-slash-type filters
			mixed_slash_traversal: {
				severity: 'high',
				cvssBaseScore: 7.5
			},
			// IIS-style Unicode dot encoding: %u002e%u002e — bypasses standard URL decoders
			unicode_dot_variant: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			// Explicit Windows System32 path target
			windows_system32_target: {
				severity: 'critical',
				cvssBaseScore: 8.3
			},
			// Linux /proc/self access — process environment and memory info
			proc_self_access: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			// Classic sensitive Unix file targets: /etc/passwd, /etc/shadow
			passwd_file_access: {
				severity: 'critical',
				cvssBaseScore: 8.5
			}
		}
	},

	nosql_injection: {
		baseSeverity: 'critical',
		cvssBaseScore: 8.5,
		owaspCategory: 'A05:2025 – Injection',
		cweId: 'CWE-943',
		mitreTechnique: 'T1190',
		variants: {
			mongo_where_js: {
				severity: 'critical',
				cvssBaseScore: 9.0
			},
			mongo_operator_body: {
				severity: 'high',
				cvssBaseScore: 8.5
			},
			mongo_operator_param: {
				severity: 'high',
				cvssBaseScore: 8.3
			},
			mongo_encoded_operator: {
				severity: 'high',
				cvssBaseScore: 8.6
			}
		}
	},

	prototype_pollution: {
		baseSeverity: 'high',
		cvssBaseScore: 7.5,
		owaspCategory: 'A08:2025 – Software and Data Integrity Failures',
		cweId: 'CWE-1321',
		mitreTechnique: 'T1190',
		variants: {
			proto_param: {
				severity: 'high',
				cvssBaseScore: 7.5
			},
			constructor_param: {
				severity: 'high',
				cvssBaseScore: 7.5
			},
			encoded_proto: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			proto_json_key: {
				severity: 'high',
				cvssBaseScore: 7.6
			},
			constructor_json_key: {
				severity: 'high',
				cvssBaseScore: 7.6
			},
			// Block 2 — Prototype Pollution robustness expansion
			// Object.prototype.x = y in parameter value — direct prototype chain assignment
			prototype_dot_notation: {
				severity: 'high',
				cvssBaseScore: 7.8
			},
			// .constructor.prototype.x in parameter value — indirect chain traversal
			constructor_prototype_chain: {
				severity: 'high',
				cvssBaseScore: 7.8
			}
		}
	},

	open_redirect: {
		baseSeverity: 'medium',
		cvssBaseScore: 6.1,
		owaspCategory: 'A01:2025 – Broken Access Control',
		cweId: 'CWE-601',
		// T1566.002: Spearphishing Link — attackers craft redirect URLs to lure
		// authenticated users to phishing pages under their own session context.
		mitreTechnique: 'T1566.002',
		variants: {
			javascript_redirect: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			data_redirect: {
				severity: 'high',
				cvssBaseScore: 7.4
			},
			protocol_relative_redirect: {
				severity: 'high',
				cvssBaseScore: 6.8
			},
			absolute_redirect: {
				severity: 'medium',
				cvssBaseScore: 6.1
			},
			encoded_redirect: {
				severity: 'high',
				cvssBaseScore: 6.8
			},
			// CRLF injection in redirect parameter — HTTP response splitting via Location header
			newline_header_redirect: {
				severity: 'high',
				cvssBaseScore: 7.6
			}
		}
	},

	ssrf: {
		baseSeverity: 'critical',
		cvssBaseScore: 8.6,
		owaspCategory: 'A10:2021 – Server-Side Request Forgery',
		cweId: 'CWE-918',
		mitreTechnique: 'T1190',
		variants: {
			// Block 1 — SSRF detector (new)
			cloud_metadata: {
				severity: 'critical',
				cvssBaseScore: 9.1
			},
			loopback: {
				severity: 'critical',
				cvssBaseScore: 8.8
			},
			internal_ip: {
				severity: 'high',
				cvssBaseScore: 8.4
			},
			protocol_smuggling: {
				severity: 'high',
				cvssBaseScore: 8.3
			},
			ssrf_param: {
				severity: 'high',
				cvssBaseScore: 8.0
			},
			// Block 2 — SSRF robustness expansion
			// Kubernetes internal service discovery hostnames
			kubernetes_metadata: {
				severity: 'critical',
				cvssBaseScore: 9.0
			},
			// Common internal service hostnames (redis, elasticsearch, etc.) without public TLD
			internal_service_probe: {
				severity: 'high',
				cvssBaseScore: 8.2
			}
		}
	}
};

export default EVENT_CATALOG;